import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthStore, useAvatarCacheKey } from '../store'
import { invoke } from '@tauri-apps/api/core'
import { isWebPlatform } from '../../utils/platform'

export interface UseAvatarUrlReturn {
  avatarSrc: string | null
  isLoading: boolean
  refreshCache: () => Promise<void>
}

// ★ Web 在线版：会话级头像缓存（blob objectURL，键 = 头像 URL + cacheKey 代数）
// 页面刷新后重建（与 Tauri 端磁盘缓存语义不同但可接受——Web 版本就无本地持久化）
const webAvatarCache = new Map<string, string>()

/** Web 端经服务端 /api/auth/avatar-proxy 下载头像（Casdoor 直连需 Bearer 头 + CORS 受限） */
async function fetchWebAvatar(cloudAvatarUrl: string, accessToken: string): Promise<string | null> {
  const cacheKey = `${cloudAvatarUrl}#${useAuthStore.getState().avatarCacheKey ?? 0}`
  const cached = webAvatarCache.get(cacheKey)
  if (cached) return cached

  const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000'
  const response = await fetch(
    `${apiBase}/api/auth/avatar-proxy?url=${encodeURIComponent(cloudAvatarUrl)}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  )
  if (!response.ok) {
    throw new Error(`头像代理请求失败 (HTTP ${response.status})`)
  }
  const blob = await response.blob()
  if (!blob.type.startsWith('image/')) {
    throw new Error(`头像代理返回非图片 (${blob.type})`)
  }
  const objectUrl = URL.createObjectURL(blob)
  // 清理旧代数的 objectURL（avatarCacheKey 递增后的旧条目）
  for (const [key, url] of webAvatarCache) {
    if (key.split('#')[0] === cloudAvatarUrl && key !== cacheKey) {
      URL.revokeObjectURL(url)
      webAvatarCache.delete(key)
    }
  }
  webAvatarCache.set(cacheKey, objectUrl)
  return objectUrl
}

export function useAvatarUrl(
  userName: string | undefined,
  cloudAvatarUrl: string | undefined
): UseAvatarUrlReturn {
  const cacheKey = useAvatarCacheKey()
  const [cachedSrc, setCachedSrc] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (!userName) {
      setCachedSrc(null)
      return
    }

    let cancelled = false

    // ★ Web 在线版：走服务端头像代理（无 Tauri invoke / 本地缓存）
    if (isWebPlatform()) {
      const accessToken = useAuthStore.getState().accessToken
      if (cloudAvatarUrl && accessToken) {
        fetchWebAvatar(cloudAvatarUrl, accessToken)
          .then((url) => { if (!cancelled) setCachedSrc(url) })
          .catch(() => { if (!cancelled) setCachedSrc(null) })
      } else {
        setCachedSrc(null)
      }
      return () => { cancelled = true }
    }

    // 优先从本地缓存读取 base64 data URL（移动端最可靠方案，不依赖文件协议和权限）
    invoke<string | null>('get_cached_avatar_base64', { username: userName })
      .then(async (base64Data) => {
        if (cancelled) return
        if (base64Data) {
          setCachedSrc(base64Data)
          return
        }
        // 无缓存，尝试下载并缓存（需要 access_token 认证）
        const accessToken = useAuthStore.getState().accessToken
        if (cloudAvatarUrl && accessToken) {
          try {
            await invoke('cache_avatar_locally', {
              avatarUrl: cloudAvatarUrl,
              username: userName,
              accessToken,
            })
            if (cancelled) return
            const newBase64 = await invoke<string>('get_cached_avatar_base64', { username: userName })
            if (!cancelled && newBase64) {
              setCachedSrc(newBase64)
              useAuthStore.getState().incrementAvatarCacheKey()
            } else if (!cancelled) {
              // 下载成功但读取失败，不 fallback 到 cloudAvatarUrl（会 401）
              setCachedSrc(null)
            }
          } catch {
            // 下载失败，不 fallback 到 cloudAvatarUrl（会 401），显示字母占位符
            if (!cancelled) {
              setCachedSrc(null)
            }
          }
        } else {
          setCachedSrc(null)
        }
      })
      .catch(() => {
        if (!cancelled) setCachedSrc(null)
      })

    return () => { cancelled = true }
  }, [userName, cacheKey, cloudAvatarUrl])

  // 仅当有有效缓存时显示，不 fallback 到会 401 的 cloudAvatarUrl
  const avatarSrc = cachedSrc || null

  const refreshCache = useCallback(async () => {
    if (!cloudAvatarUrl || !userName) return
    const accessToken = useAuthStore.getState().accessToken
    if (!accessToken) return
    setIsLoading(true)
    try {
      // ★ Web 在线版：绕过缓存强制重新拉取（bust：临时键 + 直接 fetch）
      if (isWebPlatform()) {
        await fetchWebAvatar(`${cloudAvatarUrl}?_=${Date.now()}`, accessToken)
        useAuthStore.getState().incrementAvatarCacheKey()
        return
      }
      // ★ forceRefresh：用户主动"刷新头像缓存"应强制重新下载。
      // 头像 URL 是固定路径（avatar/{username}.jpg），URL 不变但内容可能已变
      // （如他端/本端上传了新头像），不强制刷新会命中 URL 指纹返回旧图。
      await invoke('cache_avatar_locally', {
        avatarUrl: cloudAvatarUrl,
        username: userName,
        accessToken,
        forceRefresh: true,
      })
      useAuthStore.getState().incrementAvatarCacheKey()
    } finally {
      if (mountedRef.current) setIsLoading(false)
    }
  }, [cloudAvatarUrl, userName])

  return { avatarSrc, isLoading, refreshCache }
}
